import { createLoader } from '@meteor/data-hooks';
import { withAuth } from '../../user';
import { portalClient } from '../client';

export let portalsLoader = createLoader({
  name: 'portals',
  fetch: (i: { instanceId: string }) => withAuth(() => portalClient.portal.list(i)),
  mutators: {
    create: (i: { name: string; type: 'standalone' | 'embedded' }, { input }) =>
      withAuth(() =>
        portalClient.portal.create({
          instanceId: input.instanceId,
          name: i.name,
          type: i.type
        })
      )
  }
});

export let usePortals = (instanceId: string | null | undefined) => {
  let portals = portalsLoader.use(instanceId ? { instanceId } : null);

  return {
    ...portals,
    createMutator: portals.useMutator('create')
  };
};

export let portalLoader = createLoader({
  name: 'portal',
  parents: [portalsLoader],
  fetch: (i: { instanceId: string; portalId: string }) =>
    withAuth(() => portalClient.portal.get(i)),
  mutators: {
    update: (
      i: {
        name?: string;
        publicName?: string;
      },
      { input }
    ) =>
      withAuth(() =>
        portalClient.portal.update({
          ...i,
          portalId: input.portalId,
          instanceId: input.instanceId
        })
      ),

    delete: (_, { input }) =>
      withAuth(() =>
        portalClient.portal.delete({
          portalId: input.portalId,
          instanceId: input.instanceId
        })
      ),

    createDomain: (i: { domain: string; environmentId: string }, { input }) =>
      withAuth(() =>
        portalClient.portalDomain.create({
          ...i,
          portalId: input.portalId,
          instanceId: input.instanceId
        })
      ),

    deleteDomain: (i: { domainId: string }, { input }) =>
      withAuth(() =>
        portalClient.portalDomain.delete({
          ...i,
          portalId: input.portalId,
          instanceId: input.instanceId
        })
      ),

    updateDomain: (i: { environmentId: string; domainId: string }, { input }) =>
      withAuth(() =>
        portalClient.portalDomain.update({
          ...i,
          portalId: input.portalId,
          instanceId: input.instanceId
        })
      ),

    recheckDomain: (i: { domainId: string }, { input }) =>
      withAuth(() =>
        portalClient.portalDomain.recheck({
          ...i,
          portalId: input.portalId,
          instanceId: input.instanceId
        })
      ),

    createEnvironment: (i: { instanceId: string }, { input }) =>
      withAuth(() =>
        portalClient.portalEnvironment.create({
          ...i,
          portalId: input.portalId
        })
      ),

    updateEnvironment: (i: { environmentId: string; name?: string }, { input }) =>
      withAuth(() =>
        portalClient.portalEnvironment.update({
          ...i,
          portalId: input.portalId,
          instanceId: input.instanceId
        })
      ),

    createAgent: (i: { agentId: string; name: string; description: string }, { input }) =>
      withAuth(() =>
        portalClient.portalAgent.create({
          ...i,
          portalId: input.portalId,
          instanceId: input.instanceId
        })
      ),

    updateAgent: (
      i: { portalAgentId: string; name?: string; description?: string },
      { input }
    ) =>
      withAuth(() =>
        portalClient.portalAgent.update({
          ...i,
          portalId: input.portalId,
          instanceId: input.instanceId
        })
      ),

    deleteAgent: (i: { portalAgentId: string }, { input }) =>
      withAuth(() =>
        portalClient.portalAgent.delete({
          ...i,
          portalId: input.portalId,
          instanceId: input.instanceId
        })
      )
  }
});

export let usePortal = (
  instanceId: string | null | undefined,
  portalId: string | null | undefined
) => {
  let portal = portalLoader.use(portalId && instanceId ? { instanceId, portalId } : null);

  return {
    ...portal,
    updateMutator: portal.useMutator('update'),
    deleteMutator: portal.useMutator('delete'),
    createDomainMutator: portal.useMutator('createDomain'),
    deleteDomainMutator: portal.useMutator('deleteDomain'),
    updateDomainMutator: portal.useMutator('updateDomain'),
    recheckDomainMutator: portal.useMutator('recheckDomain'),
    createEnvironmentMutator: portal.useMutator('createEnvironment'),
    updateEnvironmentMutator: portal.useMutator('updateEnvironment'),
    createAgentMutator: portal.useMutator('createAgent'),
    updateAgentMutator: portal.useMutator('updateAgent'),
    deleteAgentMutator: portal.useMutator('deleteAgent')
  };
};

export let portalAuthLoader = createLoader({
  name: 'portalAuth',
  parents: [],
  fetch: (i: { instanceId: string; portalId: string }) =>
    withAuth(() => portalClient.portalAuth.get(i)),
  mutators: {
    update: (
      i: {
        authProviders: Parameters<typeof portalClient.portalAuth.update>[0]['authProviders'];
      },
      { input }
    ) =>
      withAuth(() =>
        portalClient.portalAuth.update({
          ...i,
          portalId: input.portalId,
          instanceId: input.instanceId
        })
      )
  }
});

export let usePortalAuth = (
  instanceId: string | null | undefined,
  portalId: string | null | undefined
) => {
  let portal = portalAuthLoader.use(portalId && instanceId ? { instanceId, portalId } : null);

  return {
    ...portal,
    updateAuthMutator: portal.useMutator('update')
  };
};
